import axios from "axios";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";

import { API_GET_LEAVE_MANAGER_REQUESTS, API_SQE_MANAGER_PENDING_LEAVES } from "../../config/Endpoints";
import { dispatchEvent } from "../../Hooks/useEvent";
import { Event } from "../../config/events";
import { getUser } from "../../utils/Storage";
import { getApi, putApi } from "../../utils/api";
import { getApiErrorMessage } from "../../v2/utils/api-error-message.ts";
import { showError, showSuccess } from "../../v2/utils/toast.ts";
import { useUser } from "../../v2/lib/rbac/useUser.ts";
import { requestStatusOptions } from "../../constants/requestStatusOptions";
import { LeaveSegment, resolveLeaveSegment } from "./config/leave-segments";
import { getLeaveSegmentConfig } from "./config/leave-segment-config";
import { WFH_DEDUCTION_OPTIONS } from "./config/wfh-deduction-options";
import LeaveApprovalView from "./components/LeaveApprovalView";

const approvalSchema = Yup.object().shape({
  leaveAction: Yup.string().required("Action is required"),
  reason: Yup.string().trim().min(5, "Reason must be at least 5 characters long.").required("Reason is required"),
});

const getSqeActionId = (request) =>
  request?.source === "NEW" ? request?.newRequestId : request?.legacyLeaveId;

const isWfhLeaveType = (leaveType) => {
  const normalized = String(leaveType ?? "").replace(/[^a-z]/gi, "").toUpperCase();
  return normalized === "WFH" || normalized.startsWith("WORKFROMHOME");
};

const isExceededWfhRequest = (request) =>
  ["wfhExceeded", "isWfhExceeded", "monthlyLimitExceeded", "isExceeded", "exceeded"].some(
    (key) => request?.[key] === true,
  ) || Number(request?.exceededDays) > 0 || Number(request?.workFromHomeCount) > 2;

const ApproveLeave = ({ title, description }) => {
  const [leaveList, setLeaveList] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [leaveAction, setLeaveAction] = useState("");
  const [reason, setReason] = useState("");
  const [deduction, setDeduction] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("PENDING");
  const [employeeId, setEmployeeId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [errors, setErrors] = useState({});
  const accessToken = Cookies.get("accessToken");
  const userData = JSON.parse(getUser("userData"));
  const userId = userData?.id;
  const user = useUser();
  const segment = resolveLeaveSegment({
    location: user?.location ?? userData?.location,
    empType: user?.empType ?? userData?.empType,
  });
  const leaveSegmentConfig = getLeaveSegmentConfig(segment);
  const isExceededWfh = segment === LeaveSegment.SQE &&
    isWfhLeaveType(selectedLeave?.leaveType) && isExceededWfhRequest(selectedLeave);
  const showWfhDeduction = segment === LeaveSegment.SQE &&
    isWfhLeaveType(selectedLeave?.leaveType) && !isExceededWfh && leaveAction === "APPROVED";

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    const statusQuery = selectedStatus ? `status=${selectedStatus}` : "";
    const endpoint = segment === LeaveSegment.SQE
      ? API_SQE_MANAGER_PENDING_LEAVES(userId)
      : `${API_GET_LEAVE_MANAGER_REQUESTS}/${userId}/pending-all`;
    try {
      const employeeQuery = employeeId ? `&empId=${encodeURIComponent(employeeId)}` : "";
      const res = await getApi(`${endpoint}?${statusQuery}&size=${limit}&page=${currentPage - 1}${employeeQuery}`, accessToken);
      if (res.status === 200) {
        const page = res.data?.data;
        setLeaveList(Array.isArray(page?.content) ? page.content : []);
        setTotalLeaves(page?.totalElements ?? 0);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, currentPage, employeeId, limit, segment, selectedStatus, userId]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  const closeModal = () => {
    setSelectedLeave(null);
    setLeaveAction("");
    setReason("");
    setDeduction("");
    setErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setErrors({});
      await approvalSchema.validate({ leaveAction, reason }, { abortEarly: false });
      if (showWfhDeduction && !deduction) {
        setErrors({ deduction: "Select Half Day or Full Day." });
        return;
      }
      setIsLoading(true);

      const leaveId = segment === LeaveSegment.SQE
        ? getSqeActionId(selectedLeave)
        : selectedLeave?.legacyLeaveId ?? selectedLeave?.laveId;
      if (leaveId == null) {
        showError("This leave request cannot be approved or rejected.");
        return;
      }

      const response = segment === LeaveSegment.US
        ? await putApi(`${leaveSegmentConfig.approveEndpoint}?leaveId=${leaveId}&Status=${leaveAction}`, { comment: reason }, accessToken)
        : await axios.post(
          leaveAction === "APPROVED" ? leaveSegmentConfig.approveEndpoint(leaveId) : leaveSegmentConfig.rejectEndpoint(leaveId),
          leaveAction === "APPROVED"
            ? { managerId: userId, comment: reason, ...(showWfhDeduction ? { keepDeduction: deduction === "HALF_DAY" } : {}) }
            : { managerId: userId, reason },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );

      closeModal();
      showSuccess(response?.data?.message || "Leave updated successfully.");
      await fetchList();
      dispatchEvent(Event.SIDEBAR.NOTIFY_EVENT);
    } catch (error) {
      if (error.name === "ValidationError") {
        const fieldErrors = {};
        error.inner.forEach((item) => { fieldErrors[item.path] = item.message; });
        setErrors(fieldErrors);
      } else {
        showError(getApiErrorMessage(error, "Something went wrong. Please try again."));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return <LeaveApprovalView
    title={title || "Approve Leave"} description={description} rows={leaveList} totalItems={totalLeaves}
    loading={isLoading} page={currentPage} pageSize={limit} onPageChange={setCurrentPage}
    onPageSizeChange={(size) => { setLimit(size); setCurrentPage(1); }}
    employeeId={employeeId} onEmployeeChange={(value) => { setEmployeeId(value); setCurrentPage(1); }}
    status={selectedStatus} statusOptions={requestStatusOptions}
    onStatusChange={(value) => { setSelectedStatus(value); setCurrentPage(1); }}
    isSqe={segment === LeaveSegment.SQE}
    showManager={false} showAction
    canAct={(request) => request.status === "PENDING" && (segment !== LeaveSegment.SQE || getSqeActionId(request) != null)}
    selectedLeave={selectedLeave} onSelectLeave={setSelectedLeave} onCloseModal={closeModal}
    action={leaveAction}
    onActionChange={(value) => { setLeaveAction(value); setDeduction(""); setErrors({}); }}
    deductionOptions={showWfhDeduction ? WFH_DEDUCTION_OPTIONS : undefined}
    deduction={deduction} onDeductionChange={(value) => { setDeduction(value); setErrors((previous) => ({ ...previous, deduction: "" })); }}
    deductionError={errors.deduction}
    reason={reason} onReasonChange={(value) => { setReason(value); setErrors((previous) => ({ ...previous, reason: "" })); }}
    reasonError={errors.reason} onSubmit={handleSubmit}
  />;
};

export default ApproveLeave;
